import { Component, Output, EventEmitter } from '@angular/core';
import { kendoFilterUtility } from 'app/shared/utilities/kendo-filter.utility';
import { combineLatest, of } from 'rxjs';

import { distinctUntilChanged, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Observable } from 'rxjs/internal/Observable';

import { FilterComponentRefInterface } from '@app/shared/filters/filter-componentRef.interface';
import { AlphaFilter } from '@app/shared/filters/filter.interface';
import FilterChange from '@app/shared/filters/filter-change.model';
import { FilterConfiguration } from '@app/shared/filters/filter-config.model';
import { FilterOptions } from 'app/shared/filters/filter-options.interface';
import { FilterType } from 'app/shared/components/sub-header/filter-type.model';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
})
export default class FilterComponent implements AlphaFilter, FilterComponentRefInterface {
  public config: FilterConfiguration;
  public label = '';
  public options$: Observable<FilterOptions[]>;
  public defaultValue$: Observable<FilterOptions>;
  public width = '';
  public readonly hasLogo = false;
  @Output() public valueChange = new EventEmitter<FilterChange>();

  public defaultItem!: Observable<FilterOptions>;

  public templateOptions$: Observable<FilterOptions[]> = of([]);

  public update(value: string, _global = true): void {
    this.valueChange.emit(new FilterChange(FilterType.YearAtAlpha, value));
  }

  public updateSelection(value: string, global = true) {
    this.update(value, global);
    this.config?.storageHandler(value);
    this.config?.update(value);
  }

  public init(
    idObs$: Observable<unknown>,
    transformDataObs$: Observable<unknown[]>,
    dataObs$: Observable<unknown[]>,
    labelKey = 'name',
    valueKey = 'id'
  ): void {
    this.options$ = (!!this.config.transform ? this.config.transform(transformDataObs$) : dataObs$).pipe(
      distinctUntilChanged(),
      map((options: Array<{ [key: string]: string | number }>) =>
        options.map((option) => ({ label: `${option?.[labelKey]}`, value: option?.[valueKey] }))
      )
    );

    this.initDefaultValues(idObs$);
  }

  public initDefaultValues(idObs$: Observable<unknown>) {
    this.defaultItem = this.config.disabled().pipe(map((value) => kendoFilterUtility(value, this.config)));

    this.templateOptions$ = this.options$.pipe(
      withLatestFrom(this.defaultItem),
      map(([options, defaultItem]) => {
        if (defaultItem) {
          return [defaultItem, ...options];
        }
        return options;
      }),
      tap((templateOptions) => {
        this.defaultValue$ = this.defaultItem.pipe(
          switchMap((value) => {
            return combineLatest([idObs$, of(templateOptions)]).pipe(
              map(([defaultValue, options]) => {
                let configDefault;
                if (!!defaultValue && !!options.length) {
                  configDefault =
                    options.find((option) => `${option.value}`.toLowerCase() === `${defaultValue}`?.toLowerCase()) ||
                    (this.config?.defaultValue
                      ? ({ label: this.config?.defaultValue, value: this.config?.defaultValue } as FilterOptions)
                      : null);
                }
                return configDefault || value;
              })
            );
          })
        );
      })
    );
  }
}
